import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { GlgChipsList } from 'Shared/components'

export const MONTH_VIEW_MINIMUM_WIDTH = 320

// --- Helpers ---
const DAY_LABELS = [ 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa' ]
const MONTH_NAMES = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ]
const MONTH_NAMES_SHORT = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ]

function toISO(year, zeroMonth, day) {
  return `${year}-${String(zeroMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parseISO(isoString) {
  const [ year, month, day ] = isoString.split('-').map(Number)
  return { year, month: month - 1, day }
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay()
}

function addMonths(year, month, offset) {
  const shifted = new Date(year, month + offset, 1)
  return { year: shifted.getFullYear(), month: shifted.getMonth() }
}

const MonthHeader = ({ year, month, onNavigateBack, onNavigateForward }) => {
  return (
    <div className="month-header">
      {onNavigateBack
        ? <button onClick={onNavigateBack} className="month-header-nav clickable">‹</button>
        : <span className="month-header-nav" />
      }
      <span className="month-header-title">{MONTH_NAMES[month]} {year}</span>
      {onNavigateForward
        ? <button onClick={onNavigateForward} className="month-header-nav clickable">›</button>
        : <span className="month-header-nav" />
      }
    </div>
  )
}

MonthHeader.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  onNavigateBack: PropTypes.func,
  onNavigateForward: PropTypes.func,
}

const MonthView = ({
  year,
  month,
  selectionCounts,
  onDateClick,
  onNavigateBack,
  onNavigateForward,
}) => {
  const totalDays = getDaysInMonth(year, month)
  const firstWeekday = getFirstDayOfWeek(year, month)
  const rows = []
  let currentRow = []

  for (let blank = 0; blank < firstWeekday; blank++) {
    currentRow.push(<td key={`empty-${blank}`} className="month-cell" />)
  }

  for (let date = 1; date <= totalDays; date++) {
    const dateKey = toISO(year, month, date)
    const count = selectionCounts[dateKey] || 0
    const isSelected = count > 0
    const buttonClass = isSelected ? 'day-btn selected' : 'day-btn'

    currentRow.push(
      <td key={date} className="month-cell">
        <button onClick={() => onDateClick(dateKey)} className={buttonClass}>
          {date}
          {count > 1 && <span className="day-btn-badge">{count}</span>}
        </button>
      </td>,
    )

    const cellsFilled = firstWeekday + date
    const isEndOfWeek = cellsFilled % 7 === 0
    const isLastDay = date === totalDays

    if (isEndOfWeek || isLastDay) {
      if (isLastDay) {
        while (currentRow.length < 7) {
          currentRow.push(<td key={`trailing-${currentRow.length}`} className="month-cell" />)
        }
      }
      rows.push(<tr key={`row-${rows.length}`}>{currentRow}</tr>)
      currentRow = []
    }
  }

  return (
    <div className="month">
      <MonthHeader
        year={year}
        month={month}
        onNavigateBack={onNavigateBack}
        onNavigateForward={onNavigateForward}
      />
      <div className="month-body">
        <table className="month-table">
          <thead>
          <tr>
            {DAY_LABELS.map((label) => (
              <th key={label} className="month-dow">{label}</th>
            ))}
          </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  )
}

MonthView.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  onNavigateBack: PropTypes.func,
  onNavigateForward: PropTypes.func,
  selectionCounts: PropTypes.object.isRequired,
  onDateClick: PropTypes.func.isRequired,
}

/**
 * MultiSelectCalendar — controlled component.
 *
 * @param {Object}   props
 * @param {string[]} props.selectedDates              - Array of ISO 8601 date strings ("YYYY-MM-DD"). Duplicates represent multi-selections.
 * @param {(dateKey: string) => void} props.onDateAdd    - Called with an ISO date string when a date is clicked.
 * @param {(dateKey: string) => void} props.onDateRemove - Called with an ISO date string when a chip is dismissed.
 * @param {() => void}               props.onDatesClear  - Called when "Clear All" is clicked.
 * @param {string}   [props.startDate]                - Optional ISO date string. Sets the initially visible left month. Defaults to the current month.
 * @param {number}   [props.displayedMonths=1]        - Number of side-by-side month views to display.
 * @param {string}   [props.monthsContainerClassName] - Class name for the months container. Defaults to "calendar-months".
 * @param {string}   [props.baseContainerClassName]   - Class name for the root container. Defaults to "mini-calendar".
 */
const MultiSelectCalendar = ({
  selectedDates = [],
  onDateAdd,
  onDateRemove,
  onDatesClear,
  startDate,
  displayedMonths = 1,
  monthsContainerClassName = 'calendar-months',
  baseContainerClassName = 'mini-calendar',
}) => {
  const today = new Date()
  const initialView = startDate
    ? parseISO(startDate)
    : { year: today.getFullYear(), month: today.getMonth() }

  const [ viewYear, setViewYear ] = useState(initialView.year)
  const [ viewMonth, setViewMonth ] = useState(initialView.month)

  const navigateBack = () => {
    const prev = addMonths(viewYear, viewMonth, -1)
    setViewYear(prev.year)
    setViewMonth(prev.month)
  }

  const navigateForward = () => {
    const next = addMonths(viewYear, viewMonth, 1)
    setViewYear(next.year)
    setViewMonth(next.month)
  }

  const selectionCounts = useMemo(() => {
    const counts = {}
    selectedDates.forEach((dateKey) => {
      counts[dateKey] = (counts[dateKey] || 0) + 1
    })
    return counts
  }, [ selectedDates ])

  const chipsData = useMemo(() => {
    return selectedDates
      .map((dateKey, originalIndex) => ({ dateKey, originalIndex }))
      .sort((left, right) => left.dateKey.localeCompare(right.dateKey))
      .map(({ dateKey, originalIndex }) => {
        const { month, day } = parseISO(dateKey)
        return {
          label: `${MONTH_NAMES_SHORT[month]} ${day}`,
          value: `${dateKey}::${originalIndex}`,
        }
      })
  }, [ selectedDates ])

  const visibleMonths = useMemo(() => {
    return Array.from({ length: displayedMonths }, (_, offset) =>
      addMonths(viewYear, viewMonth, offset),
    )
  }, [ viewYear, viewMonth, displayedMonths ])

  const handleChipRemove = (compositeValue) => {
    const dateKey = compositeValue.split('::')[0]
    onDateRemove?.(dateKey)
  }

  return (
    <div className={baseContainerClassName}>
      <div className={monthsContainerClassName}>
        {visibleMonths.map((monthData, index) => {
          const isFirst = index === 0
          const isLast = index === displayedMonths - 1
          return (
            <MonthView
              key={`${monthData.year}-${monthData.month}`}
              year={monthData.year}
              month={monthData.month}
              selectionCounts={selectionCounts}
              onDateClick={onDateAdd}
              onNavigateBack={isFirst ? navigateBack : undefined}
              onNavigateForward={isLast ? navigateForward : undefined}
            />
          )
        })}
      </div>

      <GlgChipsList
        data={chipsData}
        onItemRemove={handleChipRemove}
        onClear={onDatesClear}
        renderLeftTop={() => (
          <span className="cls-chips-list-title">
            {window.I18n.t('clone_leagues_sessions.leagues_dates.selected_rounds')}
          </span>
        )}
        renderRightTop={() => (
          <span className="cls-controls-text" onClick={onDatesClear}>
            {window.I18n.t('clone_leagues_sessions.leagues_dates.clear_all')}
          </span>
        )}
      />
    </div>
  )
}

MultiSelectCalendar.propTypes = {
  selectedDates: PropTypes.array.isRequired,
  onDateAdd: PropTypes.func.isRequired,
  onDateRemove: PropTypes.func.isRequired,
  onDatesClear: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  displayedMonths: PropTypes.number,
  monthsContainerClassName: PropTypes.string,
  baseContainerClassName: PropTypes.string,
}

export default MultiSelectCalendar
